import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/nurseryApi";
import { UploadSimple, Sparkle, Leaf, X } from "@phosphor-icons/react";

const SEV_COLOR = {
  healthy: "var(--success)",
  low: "#93B17D",
  moderate: "var(--accent)",
  severe: "var(--destructive)",
};
const SEV_FILL = { healthy: "20%", low: "40%", moderate: "70%", severe: "100%" };

export default function Scanner() {
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);
  const inputRef = useRef();
  const dropRef = useRef();

  useEffect(() => {
    api.get("/scan/history").then((r) => setHistory(r.data));
  }, []);

  const onPick = (f) => {
    if (!f) return;
    setFile(f);
    setResult(null);
    setErr("");
    setPreview(URL.createObjectURL(f));
  };

  const onDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove("active");
    const f = e.dataTransfer.files?.[0];
    onPick(f);
  };

  const submit = async () => {
    if (!file) return;
    setLoading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post("/scan/diagnose", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const h = await api.get("/scan/history");
      setHistory(h.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Scan failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="scanner-page" className="fade-up">
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>Field Diagnostic</div>
      <h1 className="page-title" style={{ marginTop: 8 }}>Plant Health Scanner</h1>
      <p style={{ color: "var(--muted-fg)", marginTop: 8, marginBottom: 28, maxWidth: 640, fontSize: 15 }}>Drop a leaf photo, take one with your phone, or upload from the camera roll. AI returns the diagnosis in about 5 seconds.</p>

      <div style={{ display: "grid", gridTemplateColumns: "1.05fr 1fr", gap: 24 }} className="scan-grid">
        <div className="card" style={{ padding: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--secondary)" }}>Step 1 · Upload</div>
          <div
            ref={dropRef}
            className="drop"
            style={{ marginTop: 14 }}
            onDragOver={(e) => { e.preventDefault(); dropRef.current?.classList.add("active"); }}
            onDragLeave={() => dropRef.current?.classList.remove("active")}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="drop-zone"
          >
            {preview ? (
              <img src={preview} alt="preview" style={{ maxHeight: 280, maxWidth: "100%", borderRadius: 12 }} />
            ) : (
              <div>
                <div style={{ width: 64, height: 64, borderRadius: 20, background: "var(--primary-soft)", display: "grid", placeItems: "center", margin: "0 auto" }}>
                  <UploadSimple size={28} weight="bold" color="var(--primary)" />
                </div>
                <div className="font-display" style={{ fontSize: 24, marginTop: 14 }}>Drop a leaf photo</div>
                <div style={{ fontSize: 13, color: "var(--muted-fg)", marginTop: 6 }}>or tap to choose / take with camera</div>
              </div>
            )}
            <input ref={inputRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => onPick(e.target.files?.[0])} data-testid="file-input" />
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
            <button className="btn btn-primary" onClick={submit} disabled={!file || loading} data-testid="diagnose-btn">
              <Sparkle size={14} weight="fill" /> {loading ? "Analyzing…" : "Diagnose"}
            </button>
            <button className="btn btn-outline" onClick={() => { setFile(null); setPreview(null); setResult(null); }} data-testid="clear-btn">Clear</button>
          </div>
          {err && <div className="badge badge-danger" style={{ marginTop: 12 }} data-testid="scan-error">{err}</div>}
        </div>

        <div className="card" style={{ padding: 24, minHeight: 360, background: result ? "var(--surface)" : "var(--bg-alt)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--secondary)" }}>Step 2 · Diagnosis</div>
          {!result ? (
            <div style={{ marginTop: 60, textAlign: "center", color: "var(--muted-fg)" }}>
              <div style={{ width: 76, height: 76, borderRadius: 24, background: "var(--surface)", display: "grid", placeItems: "center", margin: "0 auto", boxShadow: "var(--shadow-sm)" }}>
                <Leaf size={36} weight="fill" color="var(--primary)" />
              </div>
              <div className="font-display" style={{ fontSize: 22, marginTop: 16, color: "var(--text)" }}>Awaiting photo</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>A clear close-up of one or two affected leaves works best.</div>
            </div>
          ) : (
            <div data-testid="scan-result">
              <h2 className="font-display" style={{ fontSize: 32, marginTop: 12 }}>{result.issue}</h2>
              <div style={{ fontSize: 14, color: "var(--muted-fg)", marginTop: 4 }}>Plant: <b style={{ color: "var(--text)" }}>{result.plant}</b> · Confidence {result.confidence}%</div>
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted-fg)" }}>Severity · {result.severity}</div>
                <div className={`sev sev-${result.severity}`} style={{ marginTop: 8 }}>
                  <span style={{ width: SEV_FILL[result.severity], background: SEV_COLOR[result.severity] }} />
                </div>
              </div>
              <p style={{ marginTop: 18, fontSize: 15, lineHeight: 1.55, color: "var(--text-soft)" }}>{result.diagnosis}</p>
              <div style={{ marginTop: 16, padding: "16px 18px", background: "var(--secondary-soft)", borderRadius: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--secondary)" }}>Treatment</div>
                <ol style={{ marginTop: 8, paddingLeft: 20, fontSize: 14, lineHeight: 1.6 }}>{result.treatment.map((t, i) => <li key={i} style={{ marginBottom: 4 }}>{t}</li>)}</ol>
              </div>
              <div style={{ marginTop: 12, padding: "16px 18px", background: "var(--primary-soft)", borderRadius: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--primary)" }}>Prevention</div>
                <ol style={{ marginTop: 8, paddingLeft: 20, fontSize: 14, lineHeight: 1.6 }}>{result.prevention.map((t, i) => <li key={i} style={{ marginBottom: 4 }}>{t}</li>)}</ol>
              </div>
            </div>
          )}
        </div>
      </div>

      {history.length > 0 && (
        <div style={{ marginTop: 40 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>Recent scans</div>
          <h2 className="font-display" style={{ fontSize: 28, marginTop: 6, marginBottom: 16 }}>Scout history</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
            {history.map((h) => (
              <div key={h.id} className="card card-hover" style={{ padding: 18 }} data-testid={`history-${h.id}`}>
                <div style={{ fontSize: 11, color: "var(--muted-fg)" }}>{new Date(h.created_at).toLocaleString()}</div>
                <div className="font-display" style={{ fontSize: 20, marginTop: 6 }}>{h.issue}</div>
                <div style={{ fontSize: 13, color: "var(--muted-fg)", marginTop: 2 }}>{h.plant}</div>
                <div className={`sev sev-${h.severity}`} style={{ marginTop: 10 }}>
                  <span style={{ width: SEV_FILL[h.severity], background: SEV_COLOR[h.severity] }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <style>{`@media (max-width: 900px) { .scan-grid { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}
